/**
 * Price Range Filter (Checkbox-based, Single-Select)
 *
 * Replaces the standard price slider with predefined range checkboxes.
 * Only ONE range can be active at a time — clicking a range deselects any
 * previously selected range and selects the new one. Clicking the already-
 * selected range clears the filter.
 *
 * Compatible with Shopify Online Store 2.0 Search & Discovery AJAX filtering
 * via the theme's facets-form-component.
 */

(function initPriceRangeFilter() {
  /**
   * Initializes all .price-range-facet elements not yet initialized.
   */
  function initAll() {
    document.querySelectorAll('.price-range-facet').forEach(initComponent);
  }

  /**
   * @param {HTMLElement} container – the .price-range-facet div
   */
  function initComponent(container) {
    // Guard against double-init (e.g. called multiple times after re-render)
    if (container.dataset.priceRangeInit) return;
    container.dataset.priceRangeInit = '1';

    var checkboxes = Array.from(container.querySelectorAll('.price-range-checkbox'));
    var gteInput   = container.querySelector('.price-range-hidden-gte');
    var lteInput   = container.querySelector('.price-range-hidden-lte');
    var statusId   = container.dataset.statusId || '';
    var statusEl   = statusId ? document.getElementById(statusId) : null;
    var clearBtn   = container.querySelector('.price-range-clear-btn');

    if (!gteInput || !lteInput) return;

    /**
     * Called when a checkbox changes state.
     * Enforces single-select: unchecks all other checkboxes, then writes the
     * selected range's GTE/LTE into the hidden inputs and triggers the filter.
     *
     * @param {HTMLInputElement} changedCb - The checkbox that was just changed.
     */
    function onCheckboxChange(changedCb) {
      // If the changed checkbox is now checked, uncheck all others
      if (changedCb.checked) {
        checkboxes.forEach(function(cb) {
          if (cb !== changedCb) cb.checked = false;
        });
      }

      var checked = changedCb.checked ? changedCb : null;

      if (!checked) {
        // Nothing selected → clear price params (facets.js strips empty strings)
        gteInput.value = '';
        lteInput.value = '';
      } else {
        // data-gte / data-lte are stored in cents (minor units)
        var gte    = parseInt(checked.dataset.gte, 10) || 0;
        var lteRaw = checked.dataset.lte;
        var lte    = (lteRaw !== '' && lteRaw != null) ? parseInt(lteRaw, 10) : null;

        // Shopify URL params expect major currency units (e.g. "299.00" not 29900)
        gteInput.value = (gte > 0) ? (gte / 100).toFixed(2) : '';
        lteInput.value = (lte !== null) ? (lte / 100).toFixed(2) : '';
      }

      // Reflect selection state in the accordion summary badge (0 or 1)
      updateStatus(checked ? 1 : 0);

      // Toggle clear button visibility
      if (clearBtn) {
        clearBtn.classList.toggle('price-range-clear-btn--hidden', !checked);
      }

      // Trigger AJAX filter update via the theme's facets-form-component
      triggerFilterUpdate(container);
    }

    /**
     * Updates the selection-count badge in the accordion summary.
     * @param {number} count
     */
    function updateStatus(count) {
      if (!statusEl) return;
      if (count === 0) {
        statusEl.textContent = '';
        statusEl.classList.remove('bubble', 'facets__bubble');
      } else {
        statusEl.textContent = String(count);
        statusEl.classList.add('bubble', 'facets__bubble');
      }
    }

    /**
     * Finds the closest facets-form-component and calls updateFilters().
     * @param {HTMLElement} el
     */
    function triggerFilterUpdate(el) {
      var facetsForm = el.closest('facets-form-component');
      if (facetsForm && typeof facetsForm.updateFilters === 'function') {
        facetsForm.updateFilters();
      }
    }

    // ── Attach listeners ────────────────────────────────────────────────────

    // Native checkbox change (fires when clicking the input or its label)
    checkboxes.forEach(function(cb) {
      cb.addEventListener('change', function() {
        onCheckboxChange(cb);
      });
    });

    // Delegated click on the container: only handle clicks on the row *background*
    // (i.e. not on the <input> or <label> which the browser already handles).
    container.addEventListener('click', function(event) {
      // Walk the full event path to check if any ancestor is <label> or <input>
      var path = event.composedPath ? event.composedPath() : [];
      for (var i = 0; i < path.length; i++) {
        var el = path[i];
        if (!el.tagName) continue;
        var tag = el.tagName.toUpperCase();
        if (tag === 'INPUT' || tag === 'LABEL') return; // browser handles it
        if (el === container) break; // reached our container — nothing matched
      }

      // Find which .price-range-row was clicked
      var row = event.target && event.target.closest
        ? event.target.closest('.price-range-row')
        : null;
      if (!row) return;

      var cb = row.querySelector('.price-range-checkbox');
      if (cb && !cb.disabled) {
        cb.checked = !cb.checked;
        onCheckboxChange(cb);
      }
    });

    // Keyboard: Space / Enter when a row div has focus
    container.querySelectorAll('.price-range-row').forEach(function(row) {
      row.addEventListener('keydown', function(event) {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          var cb = row.querySelector('.price-range-checkbox');
          if (cb && !cb.disabled) {
            cb.checked = !cb.checked;
            onCheckboxChange(cb);
          }
        }
      });
    });

    // "Clear" button inside the filter panel
    if (clearBtn) {
      clearBtn.addEventListener('click', function() {
        checkboxes.forEach(function(cb) { cb.checked = false; });
        gteInput.value = '';
        lteInput.value = '';
        updateStatus(0);
        clearBtn.classList.add('price-range-clear-btn--hidden');
        triggerFilterUpdate(container);
      });
    }
  }

  // ── Initialization ─────────────────────────────────────────────────────────

  // Run on initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init after Shopify theme editor section loads/reloads
  document.addEventListener('shopify:section:load', function() {
    initAll();
  });

  // Re-init when the theme's AJAX section renderer inserts new DOM nodes
  // (facets.js / section-renderer.js swaps in new HTML after each filter change)
  var observer = new MutationObserver(function(mutations) {
    var needsInit = false;
    mutations.forEach(function(m) {
      m.addedNodes.forEach(function(node) {
        if (node.nodeType !== 1) return;
        if (
          node.classList && node.classList.contains('price-range-facet') ||
          (typeof node.querySelector === 'function' && node.querySelector('.price-range-facet'))
        ) {
          needsInit = true;
        }
      });
    });
    if (needsInit) initAll();
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
